<template>
    <div>
        <r-form
                v-if="form"
                ref="form"
                :column-size="1"
                :crud="true"
                :disabled.sync="disabled"
                :validators="validators"
                :showHeader="false"
                :hideCancel="!contact && !form.propertyContactId.value"
                :saveDisabled="!form.propertyContactId.value"
                title="contact"
                @submit="save"
                @cancel="onCancel"
                v-model="form"
                hide-loading
                read-only-disabled-mode
                @update:loading="loading = $event"
        >
            <template slot="bottom-buttons">
                <b-btn variant="yellow"
                       class="btn-arrow"
                       v-if="!disabled"
                       @click="openModal('add')"
                >
                    Add<span class="d-none d-sm-inline"> contact</span>
                    <font-awesome-icon
                            :icon="['far', 'arrow-right']">
                    </font-awesome-icon>
                </b-btn>
            </template>
            <template slot="buttons"
                      v-if="!$can($perm.PROPERTIES_EDIT_PROPERTIES, $perm.PERMISSION_TYPE_PROPERTIES) && !$can($perm.PROPERTIES_EDIT_PROPERTY_CONTACTS, $perm.PERMISSION_TYPE_PROPERTIES)"
            >
                <div></div>
            </template>
        </r-form>
        <add-contact-modal
                v-if="modals.add.visible"
                :property-id="property.propertyId"
                :show.sync="modals.add.visible"
                @newContactAdded="onNewContactAdded"
        >
        </add-contact-modal>
    </div>
</template>

<script>
import RForm from '@/components/Form/RiskhubForm';
import PropertyContact from '@/services/Property/PropertyContact';
import { cloneDeep, forEach, isObject } from 'lodash';
import { form as PropertyContactForm, validators as PropertyContactFormValidators } from '@/services/Property/PropertyContactForm';
import Property from '@/services/Property/Property';
import AddContactModal from '@/components/Property/Modal/AddContactModal';
import ErrorMixin from '@/components/Notifications/ErrorMixin';
import dayjs from 'dayjs';

export default {
  name: 'Property-Contacts',
  components: {
    RForm,
    AddContactModal,
  },
  mixins: [ErrorMixin],
  props: {
    property: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      disabled: true,
      hideCancel: true,
      loading: false,
      form: null,
      validators: PropertyContactFormValidators,
      contacts: [],
      contact: [],
      modals: {
        add: {
          visible: false,
        },
      },

    };
  },
  watch: {
    'form.propertyContactId.value': {
      handler(newValue) {
        /**
         *  Do not change any values while loading.
         * This is useful for when we save the property details and go back
         * to the "disabled" property form
         */
        if (this.loading) {
          return;
        }

        if (!newValue) {
          return;
        }

        const { propertyContactId } = newValue;
        if (!propertyContactId) {
          return;
        }

        this.populateFields(this.form, newValue);
      },
    },

    loading(newValue) {
        this.$emit('update:loading', newValue);
    },

    property: {
      handler(property) {
        this.form = null;
        this.disabled = true;
        this.fetch(property.propertyContactId);
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    async fetch(propertyContactId) {
      this.loading = true;

      try {
        const { data } = await PropertyContact.list();
        this.contacts = data.data;
        this.contact = this.contacts.find(item => item.propertyContactId === propertyContactId);

        this.setForm();
      } catch (e) {
        this.$router.push({ name: 'properties' }, () => {
          this.$notify({
            title: 'Property contact not found',
            text: 'Property contact could not be found.',
            type: 'error',
          });
        });
      } finally {
        this.loading = false;
      }
    },

    populateFields(form, values) {
      forEach(values, (value, key) => {
        if (key === 'propertyContactId') {
          return true;
        }
        if (key in form) {
          this.$set(form[key], 'value', value);
        }
      });

      return form;
    },

    setForm() {
      let form = PropertyContactForm();
      const contact = this.contact;
      form = this.populateFields(form, contact);
      form.propertyContactId.options = this.contacts;
      form.propertyContactId.value = this.contact;

      forEach(form, (value, key) => {
        if (key === 'propertyContactId') {
          return true;
        }
        if (key in form) {
          form[key].hidden = values => values.propertyContactId.value === 0 || !isObject(values.propertyContactId.value);
        }
      });

      if (!this.contact) {
        this.disabled = false;
      }

      this.form = form;
    },

    /**
     * Opens a modal by name (in data -> modals.[name])
     */
    openModal(name) {
      this.modals[name].visible = true;
      return true;
    },

    async save(form) {
      this.loading = true;

      const params = {};
      forEach(form, (object, field) => {
        const { value, disabled } = object;
        if (value === null) {
          return true;
        }

        if (disabled) {
          return true;
        }

        params[field] = object.value;
      });

      try {
        const { data } = await PropertyContact.update(params);
        await Property.update(
            this.property.propertyId,
            {
              propertyContactId: data.data.propertyContactId,
              propertyContactUpdatedDate: dayjs().format('YYYY-MM-DD DD:HH:mm'),
            }
          );

        this.$emit('update:propertyContactId', data.data.propertyContactId);

        this.$notify({
          title: 'Property contact updated',
          message: 'Property contact has been successfully updated',
          type: 'success',
        });

        this.disabled = true;

      } catch (e) {
        this.showValidationError(e);
      } finally {
        this.loading = false;
      }
    },

    onNewContactAdded(newContactId) {
      this.disabled = true;
      this.$emit('update:propertyContactId', newContactId);
    },

    onCancel() {
      if (!this.contact) {
        this.disabled = false;
      }
    }
  },
};
</script>
